import React, { useMemo, useRef } from 'react'

import { gql } from '@apollo/client'
import { withMediaViewer } from '@place/media-viewer'

import FlickingArea from '@web/components/FlickingArea/FlickingArea'
import { getNClickCode } from '@web/components/MediaList/index.helper'
import { MediaListNClickArea } from '@web/components/MediaList/index.interface'
import { getHighestRatio } from '@web/components/MediaList/lazy.helper'
import { MediaList_ReviewGroupFragment, MediaType } from '@web/gql/graphql'
import useOpenMediaViewer from '@web/hooks/useOpenMediaViewer'
import { convertReviewGroupToViewer } from '@web/hooks/useOpenMediaViewer.helper'
import { isTypename } from '@web/libs/apollo/typeGuard'
import nClicks from '@web/libs/nlog/click/codes'

import FlickingImageItem from './FlickingImageItem'
import FlickingVideoItem from './FlickingVideoItem'
import styles from './index.module.scss'
import { MediaListArea } from './MediaListArea'

interface MediaListProps {
  reviewGroup: MediaList_ReviewGroupFragment
  nClickArea: MediaListNClickArea
  isTheme?: boolean
  isThemeMap?: boolean
  useLazyLoading?: boolean
  onClick?: () => void
}

export const MediaList = ({
  reviewGroup,
  nClickArea,
  isTheme,
  isThemeMap = false,
  useLazyLoading,
  onClick,
}: MediaListProps) => {
  const { mediaItems } = reviewGroup

  const mediaListRef = useRef(null)
  const openMediaViewer = useOpenMediaViewer()

  const mediaViewerItems = convertReviewGroupToViewer(reviewGroup)
  const openMediaViewerByIndex = (index: number) => openMediaViewer(index, mediaViewerItems)

  const highestRatio = useMemo(() => getHighestRatio(mediaItems), [mediaItems])

  if (mediaViewerItems.length === 0) return null

  const flickingCommonProps = {
    openImageViewer: openMediaViewerByIndex,
    isTheme,
    isThemeMap,
    useLazyLoading,
  }

  return (
    <MediaListArea isTheme={isTheme} isThemeMap={isThemeMap} hasMultiple={mediaItems.length > 1}>
      <div ref={mediaListRef} className={styles.img_area}>
        {mediaItems.length === 1 ? (
          isTypename(mediaItems[0], 'ImageItem') ? (
            <FlickingImageItem
              imageItem={mediaItems[0]}
              idx={0}
              nClickCode={getNClickCode(MediaType.Image, nClickArea)}
              highestRatio={highestRatio}
              onClick={onClick}
              {...flickingCommonProps}
            />
          ) : (
            <FlickingVideoItem
              videoItem={mediaItems[0]}
              idx={0}
              nClickCode={getNClickCode(MediaType.Video, nClickArea)}
              highestRatio={highestRatio}
              onClick={onClick}
              {...flickingCommonProps}
            />
          )
        ) : (
          <div className={styles.photo_flicking_area}>
            <FlickingArea
              options={{
                moveType: 'snap',
                align: 'prev',
              }}
              flickingCode={nClicks[nClickArea].PICSWIPE}
              paginationType={isTheme ? undefined : 'fraction'}
            >
              {mediaItems.map((mediaItem, idx) =>
                isTypename(mediaItem, 'ImageItem') ? (
                  <FlickingImageItem
                    key={mediaItem.id}
                    imageItem={mediaItem}
                    idx={idx}
                    nClickCode={getNClickCode(MediaType.Image, nClickArea)}
                    highestRatio={highestRatio}
                    isMulti
                    onClick={onClick}
                    {...flickingCommonProps}
                  />
                ) : (
                  <FlickingVideoItem
                    key={mediaItem.id}
                    videoItem={mediaItem}
                    idx={idx}
                    nClickCode={getNClickCode(MediaType.Video, nClickArea)}
                    highestRatio={highestRatio}
                    isMulti
                    onClick={onClick}
                    {...flickingCommonProps}
                  />
                ),
              )}
            </FlickingArea>
          </div>
        )}
      </div>
    </MediaListArea>
  )
}

MediaList.fragments = {
  review: gql`
    fragment MediaList_ReviewGroup on ReviewGroup {
      id
      mediaItems {
        id
        type
        width
        height
        ...FlickingImageItem_MediaItem
        ...FlickingVideoItem_MediaItem
      }
      ...useOpenMediaViewer_ReviewGroup
    }
  `,
}

export default withMediaViewer<MediaListProps>(MediaList)
